import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import {
  IconRefresh,
  IconMessage,
  IconAlertTriangle,
  IconCircleCheck,
  IconClock,
  IconDeviceMobile,
  IconFilter,
  IconEye,
} from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { useSmsLogs, useSmsStats, useUnmatchedSms, useFraudAlerts } from "@/hooks/use-sms-logs";
import { wsManager } from "@/lib/websocket";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";

export const Route = createFileRoute("/sms-logs/")({
  component: SmsLogsPage,
});

function SmsLogsPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <SmsLogsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function SmsLogsContent() {
  const [activeTab, setActiveTab] = useState("all");
  const [operatorFilter, setOperatorFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  const { data: smsLogs, isLoading: loadingLogs, refetch: refetchLogs } = useSmsLogs({
    operator: operatorFilter !== "all" ? operatorFilter : undefined,
    status: statusFilter !== "all" ? statusFilter : undefined,
    limit: 50,
  });

  const { data: stats, refetch: refetchStats } = useSmsStats();
  const { data: unmatchedSms, refetch: refetchUnmatched } = useUnmatchedSms(50);
  const { data: fraudAlerts, refetch: refetchFraud } = useFraudAlerts({ limit: 50 });

  // WebSocket real-time updates
  useEffect(() => {
    const handleSmsReceived = (data: any) => {
      toast.info(`New SMS received: ${data.amount} BDT from ${data.simPhoneNumber}`);
      refetchLogs();
      refetchStats();
    };

    const handleSmsMatched = (data: any) => {
      toast.success(`SMS matched to order ${data.orderId}`);
      refetchLogs();
    };

    const handleSmsAutoApproved = (data: any) => {
      toast.success(`Transaction auto-approved: ${data.amount} BDT`);
      refetchLogs();
      refetchStats();
    };

    const handleFraudDetected = (data: any) => {
      toast.error(`Fraud detected: ${data.fraudFlags.join(", ")}`, {
        duration: 5000,
      });
      refetchFraud();
      refetchLogs();
    };

    const handleManualReview = (data: any) => {
      toast.warning(`Manual review required: ${data.reason}`);
      refetchUnmatched();
      refetchLogs();
    };

    // Subscribe to WebSocket events
    wsManager.on("sms_received", handleSmsReceived);
    wsManager.on("sms_matched", handleSmsMatched);
    wsManager.on("sms_auto_approved", handleSmsAutoApproved);
    wsManager.on("sms_fraud_detected", handleFraudDetected);
    wsManager.on("sms_manual_review_required", handleManualReview);

    return () => {
      wsManager.off("sms_received", handleSmsReceived);
      wsManager.off("sms_matched", handleSmsMatched);
      wsManager.off("sms_auto_approved", handleSmsAutoApproved);
      wsManager.off("sms_fraud_detected", handleFraudDetected);
      wsManager.off("sms_manual_review_required", handleManualReview);
    };
  }, [refetchLogs, refetchStats, refetchFraud, refetchUnmatched]);

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      pending: "orange",
      matched: "blue",
      used: "green",
      ignored: "gray",
      duplicate: "red",
    };
    return colors[status] || "gray";
  };

  const getOperatorColor = (operator: string) => {
    const colors: Record<string, string> = {
      nagad: "orange",
      bkash: "pink",
      rocket: "purple",
    };
    return colors[operator?.toLowerCase()] || "gray";
  };

  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">SMS Logs Monitoring</h1>
          <p className="text-muted-foreground mt-1">
            Real-time SMS transaction matching and fraud detection
          </p>
        </div>
        <Button variant="outline" onClick={() => refetchLogs()}>
          <IconRefresh className="h-4 w-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Stats Summary */}
      {stats && (stats as any) && (
        <div className="grid gap-4 md:grid-cols-4">
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Last 24 Hours</p>
              <IconMessage className="h-4 w-4 text-muted-foreground" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {(stats as any).last24Hours?.statusCounts?.total || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-1">
              {(stats as any).last24Hours?.statusCounts?.used || 0} auto-approved
            </p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Approval Rate</p>
              <IconCircleCheck className="h-4 w-4 text-green-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {(stats as any).lastHour?.approvalRate || 0}%
            </p>
            <p className="text-sm text-muted-foreground mt-1">Last hour</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Fraud Alerts</p>
              <IconAlertTriangle className="h-4 w-4 text-red-500" />
            </div>
            <p className="text-2xl font-bold mt-2">
              {(stats as any).last24Hours?.fraudCounts?.total || 0}
            </p>
            <p className="text-sm text-muted-foreground mt-1">Requires attention</p>
          </div>
          <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium text-muted-foreground">Operators</p>
              <IconDeviceMobile className="h-4 w-4 text-blue-500" />
            </div>
            <div className="flex gap-2 mt-2 flex-wrap">
              {(stats as any).last24Hours?.operatorCounts?.map((op: any) => (
                <Badge key={op.operator} variant="outline" className={`text-${getOperatorColor(op.operator)}-500`}>
                  {op.operator}: {op.count}
                </Badge>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <IconFilter className="h-5 w-5 text-muted-foreground" />
        <Select value={operatorFilter} onValueChange={setOperatorFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by operator" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Operators</SelectItem>
            <SelectItem value="nagad">Nagad</SelectItem>
            <SelectItem value="bkash">bKash</SelectItem>
            <SelectItem value="rocket">Rocket</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="matched">Matched</SelectItem>
            <SelectItem value="used">Used</SelectItem>
            <SelectItem value="ignored">Ignored</SelectItem>
            <SelectItem value="duplicate">Duplicate</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">
            All SMS Logs
            {smsLogs && (
              <Badge variant="secondary" className="ml-2">
                {smsLogs.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="unmatched">
            Unmatched
            {unmatchedSms && unmatchedSms.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {unmatchedSms.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="fraud">
            Fraud Alerts
            {fraudAlerts && fraudAlerts.length > 0 && (
              <Badge variant="destructive" className="ml-2">
                {fraudAlerts.length}
              </Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* All SMS Logs Tab */}
        <TabsContent value="all" className="mt-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Agent Phone</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Fraud</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingLogs ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      Loading SMS logs...
                    </TableCell>
                  </TableRow>
                ) : !smsLogs || smsLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="text-center py-8">
                      No SMS logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  smsLogs.map((sms: any) => (
                    <TableRow key={sms.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <IconClock className="h-4 w-4 text-muted-foreground" />
                          <span className="text-sm">
                            {formatDistanceToNow(new Date(sms.timestamp), {
                              addSuffix: true,
                            })}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.simPhoneNumber}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-${getOperatorColor(sms.operator)}-500`}>
                          {sms.operator?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">৳{parseFloat(sms.amount).toFixed(2)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.senderNumber || "N/A"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.transactionId || "N/A"}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`border-${getStatusColor(sms.status)}-500 text-${getStatusColor(sms.status)}-500`}>
                          {sms.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {sms.fraudFlags && sms.fraudFlags.length > 0 ? (
                          <Badge variant="destructive">
                            {sms.fraudFlags.length} flags
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">None</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <IconEye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Unmatched SMS Tab */}
        <TabsContent value="unmatched" className="mt-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Agent Phone</TableHead>
                  <TableHead>Operator</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Customer</TableHead>
                  <TableHead>Transaction ID</TableHead>
                  <TableHead>Balance After</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!unmatchedSms || unmatchedSms.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <IconCircleCheck className="h-12 w-12 text-green-500" />
                        <p className="font-medium">All SMS Matched!</p>
                        <p className="text-sm text-muted-foreground">
                          No unmatched SMS found
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  unmatchedSms.map((sms: any) => (
                    <TableRow key={sms.id}>
                      <TableCell>
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(sms.timestamp), {
                            addSuffix: true,
                          })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.simPhoneNumber}</div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className={`text-${getOperatorColor(sms.operator)}-500`}>
                          {sms.operator?.toUpperCase()}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">৳{parseFloat(sms.amount).toFixed(2)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.senderNumber || "N/A"}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.transactionId || "N/A"}</div>
                      </TableCell>
                      <TableCell>
                        <div>৳{parseFloat(sms.balanceAfter).toFixed(2)}</div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <IconEye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Fraud Alerts Tab */}
        <TabsContent value="fraud" className="mt-4">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Time</TableHead>
                  <TableHead>Agent Phone</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Fraud Flags</TableHead>
                  <TableHead>Matched Order</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {!fraudAlerts || fraudAlerts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <div className="flex flex-col items-center gap-2">
                        <IconCircleCheck className="h-12 w-12 text-green-500" />
                        <p className="font-medium">No Fraud Alerts!</p>
                        <p className="text-sm text-muted-foreground">
                          No fraud detected in recent transactions
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  fraudAlerts.map((sms: any) => (
                    <TableRow key={sms.id} className="bg-red-50 dark:bg-red-950/20">
                      <TableCell>
                        <span className="text-sm">
                          {formatDistanceToNow(new Date(sms.timestamp), {
                            addSuffix: true,
                          })}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="font-mono text-sm">{sms.simPhoneNumber}</div>
                      </TableCell>
                      <TableCell>
                        <div className="font-semibold">৳{parseFloat(sms.amount).toFixed(2)}</div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {sms.fraudFlags?.map((flag: string) => (
                            <Badge key={flag} variant="destructive" className="text-xs">
                              {flag}
                            </Badge>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        {sms.matchedOrderId ? (
                          <div className="font-mono text-sm">{sms.matchedOrderId}</div>
                        ) : (
                          <span className="text-muted-foreground text-sm">N/A</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">{sms.status}</Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button variant="ghost" size="sm">
                          <IconEye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Auto-refresh indicator */}
      <p className="text-sm text-muted-foreground text-center">
        Real-time updates enabled via WebSocket
      </p>
    </div>
  );
}
